import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { IoIosArrowBack, IoMdInformationCircle } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Input, Select, Textarea, TimeInput } from '~/components';
import { useSequency } from '~/hooks/sequency';
import { useStudent } from '~/hooks/student';
import { Prescription } from '~/models/Sequencies';
import api from '~/services/api';
import { formatHMS, getSeconds } from '~/utils';
import { formatProfileCode } from '~/utils/formatProfileCode';
import getValidationErrors from '~/utils/getValidationErrors';
import { secondsToStringFormat } from '~/utils/secondsToStringFormat';

import { IProgram } from '../../../interface';
import { DivisionData, DivisionDataResults } from '../PrescriptionScreen/types';
import Modal from './Modal';
import {
  IControlPanel,
  IControlPanelForm,
  IDivisionData,
  IRouteParams,
  ISelectProps,
} from './types';

import * as S from './styles';

export function ControlPanel() {
  const { billingId } = useParams<IRouteParams>();
  const { goBack, location } = useHistory();
  const { sequencies, updateSequencies } = useSequency();
  const { studentData } = useStudent();
  // const [program] = useState(location.state as DivisionDataResults);

  const { division, programId } = useState(
    location.state as {
      division: DivisionDataResults;
      programId: number;
    },
  )[0];

  const formRef = useRef<FormHandles>(null);

  const [program, setProgram] = useState({} as IProgram);
  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false);
  // const [divisionId, setDivisionId] = useState<number>(0);
  const [panelOfControlId, setPanelOfControlId] = useState<number>();

  const [isLoading, setIsLoading] = useState(false);
  const [initialData, setInitialData] = useState<IControlPanelForm>(
    {} as IControlPanelForm,
  );
  const [defaultInitialCardio, setDefaultInitialCardio] =
    useState<ISelectProps>({} as ISelectProps);
  const [defaultFinalCardio, setDefaultFinalCardio] = useState<ISelectProps>(
    {} as ISelectProps,
  );
  const [sequencyPrescriptions, setSequencyPrescriptions] = useState<
    Prescription[]
  >([]);
  const [toggle, setToggle] = useState<boolean>(false);
  const [toggleTotalTime, setToggleTotalTime] = useState(false);
  const [studentAvailableTime, setStudentAvailableTime] = useState<string>('');
  const [intervalBetweenSequency, setIntervalBetweenSequency] =
    useState<number>(0);
  const [trainingStart, setTrainingStart] = useState<number>(0);
  const [trainingEnd, setTrainingEnd] = useState<number>(0);
  const [trainingTimeOfExecution, setTrainingTimeOfExecution] =
    useState<number>(0);
  const [
    trainingTimeOfExecutionDefaultValue,
    setTrainingTimeOfExecutionDefaultValue,
  ] = useState<string>(initialData.time_of_execution);
  const [workoutTimeDefaultValue, setWorkoutTimeDefaultValue] =
    useState<string>(initialData.workout_time);
  const [workoutTime, setWorkoutTime] = useState<number>(0);
  const [recoveryTotalDefaultValue, setRecoveryTotalDefaultValue] =
    useState<string>(initialData.recovery_total);
  const [recoveryTotal, setRecoveryTotal] = useState<number>(0);
  const [totalRestDefaultValue, setTotalRestDefaultValue] = useState<string>(
    initialData.total_rest,
  );
  const [totalRest, setTotalRest] = useState<number>(0);
  const [totalTimeDefaultValue, setTotalTimeDefaultValue] = useState<string>(
    initialData.total_time,
  );
  const [totalTime, setTotalTime] = useState<number>(0);

  const hasTwoOrMoreSequencies = useMemo(
    () => sequencies.length > 1,
    [sequencies],
  );

  const handleGetDivisions = useCallback(async () => {
    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setProgram(response.data.header);
  }, [programId]);

  useEffect(() => {
    handleGetDivisions();
  }, [handleGetDivisions]);

  // load divisionId
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<IDivisionData>(
        `training-programs/${programId}/divisions`,
      );

      const pocId = program?.controlPanel?.id;

      pocId && setPanelOfControlId(pocId);

      if (data?.header?.datasheet?.available_time) {
        // console.log('data.header:', data?.header);
        const formattedValue = `
        00:${
          data.header.datasheet.available_time.replace(`"`, '').split('-')[1]
        }:00
          `;

        console.log('formattedValue:', formattedValue);
        setStudentAvailableTime(formattedValue);
      }
    }

    fetchData();

    setToggle((prev) => !prev);
  }, [program, programId]);

  // console.log('toggleTotalTime:', toggleTotalTime);
  // console.log('studentAvailableTime:', studentAvailableTime);

  // loadSequencies
  useEffect(() => {
    updateSequencies(division.id);
  }, [updateSequencies, division]);

  useEffect(() => {
    const prescriptions: any[] = [];

    sequencies.forEach((sequency) =>
      sequency.prescriptions.forEach((prescription) =>
        prescriptions.push(prescription),
      ),
    );

    setSequencyPrescriptions(prescriptions);

    setToggle((prev) => !prev);
  }, [sequencies]);

  // calculateTotalCardioTime
  useEffect(() => {
    const initial =
      formRef.current?.getFieldValue('training_start').length > 5
        ? formRef.current?.getFieldValue('training_start')
        : `${formRef.current?.getFieldValue('training_start')}:00`;

    const final =
      formRef.current?.getFieldValue('training_end').length > 5
        ? formRef.current?.getFieldValue('training_end')
        : `${formRef.current?.getFieldValue('training_end')}:00`;

    const calc = getSeconds(initial) + getSeconds(final);

    formRef.current?.setFieldValue('time_of_execution', formatHMS(calc));
  }, [toggle, sequencyPrescriptions]);

  // set data from prescriptions
  useEffect(() => {
    const numberOfSeries =
      sequencyPrescriptions.map(
        (prescriptions) => prescriptions.number_of_series,
      ).length > 0
        ? sequencyPrescriptions
            .map((prescriptions) => prescriptions.number_of_series)
            .reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0))
        : 0;

    formRef.current?.setFieldValue('number_of_series', numberOfSeries);

    const numberOfRepetitions =
      sequencyPrescriptions.filter(
        (prescriptions) => prescriptions.volume_total,
      ).length > 0
        ? sequencyPrescriptions
            .map((prescriptions) => prescriptions.volume_total)
            .reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0))
        : 0;

    formRef.current?.setFieldValue(
      'number_of_repetitions',
      numberOfRepetitions,
    );

    const workOutTime = sequencyPrescriptions.map(
      (prescriptions) => prescriptions.total_action,
    );

    const workOutTimeNumber =
      workOutTime.length > 0 &&
      workOutTime
        ?.map((value) => getSeconds(value))
        ?.reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0));

    workOutTime.length > 0 &&
      formRef.current?.setFieldValue(
        'workout_time',
        workOutTimeNumber && formatHMS(workOutTimeNumber),
      );
    setWorkoutTimeDefaultValue(formatHMS(workOutTimeNumber as number));

    const recoveryTotal2 = sequencyPrescriptions.map(
      (prescriptions) => prescriptions.recovery_total,
    );

    const recoveryTotalNumber =
      recoveryTotal2.length > 0 &&
      recoveryTotal2
        ?.map((value) => getSeconds(value))
        ?.reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0));

    recoveryTotal2.length > 0 &&
      formRef.current?.setFieldValue(
        'recovery_total',
        recoveryTotalNumber && formatHMS(recoveryTotalNumber),
      );
    setRecoveryTotalDefaultValue(formatHMS(recoveryTotalNumber as number));

    workOutTimeNumber &&
    recoveryTotalNumber &&
    workOutTimeNumber +
      recoveryTotalNumber +
      getSeconds(formRef.current?.getFieldValue('time_of_execution') || '') >
      getSeconds(studentAvailableTime || '')
      ? setToggleTotalTime(true)
      : setToggleTotalTime(false);
  }, [toggle, sequencyPrescriptions, studentAvailableTime]);

  const cardioVariations = useMemo(
    () => [
      {
        label: 'AIR BIKE',
        value: 'AIR BIKE',
      },
      {
        label: 'BIKE DE SPINNING',
        value: 'BIKE DE SPINNING',
      },
      {
        label: 'BIKE ERGOMÉTRICA',
        value: 'BIKE ERGOMÉTRICA',
      },
      {
        label: 'CORRIDA ESTACIONÁRIA',
        value: 'CORRIDA ESTACIONÁRIA',
      },
      {
        label: 'CORRIDA MOUNTAIN CLIMB',
        value: 'CORRIDA MOUNTAIN CLIMB',
      },
      {
        label: 'ESCADA',
        value: 'ESCADA',
      },
      {
        label: 'ESTEIRA',
        value: 'ESTEIRA',
      },
      {
        label: 'JUMP',
        value: 'JUMP',
      },
      {
        label: 'NA',
        value: 'NA',
      },
      {
        label: 'REMO',
        value: 'REMO',
      },
      {
        label: 'RUNNING',
        value: 'RUNNING',
      },
      {
        label: 'TRANSPORT',
        value: 'TRANSPORT',
      },
    ],
    [],
  );

  // fetch controlPanel data
  const fetchControlPanel = useCallback(async () => {
    const { data } = await api.get<IControlPanel>(
      `divisions/${division.id}/control-panel`,
    );

    console.log('data:', data);
    console.log('sequencyPrescriptions:', sequencyPrescriptions);
    // TODO Pegar quantidade de sequências e somar o total rest

    const totalRest2 = sequencyPrescriptions?.reduce(
      (acumulator, sequency) =>
        acumulator + getSeconds(sequency?.recovery_total),
      0,
    );

    console.log('totalRest2:', formatHMS(totalRest2));

    if (data) {
      const initialFormData = {
        ...data,
        interval_between_sequence: formatHMS(data.interval_between_sequence),
        training_start: formatHMS(data.training_start),
        training_end: formatHMS(data.training_end),
      };

      setInitialData(initialFormData);

      const initialCardio = cardioVariations.filter(
        (variation) => variation.label === data.cardio_aerobic,
      )[0];

      console.log('initialFormData:', initialFormData);

      setDefaultInitialCardio(initialCardio);
      setTrainingTimeOfExecutionDefaultValue(initialFormData.time_of_execution);
      setTotalRestDefaultValue(initialFormData.total_rest);
      // console.log('initialFormData.total_time:', initialFormData.total_time);

      setTotalTimeDefaultValue(initialFormData.total_time);

      const finalCardio = cardioVariations.filter(
        (variation) => variation.label === data.cardio_aerobic_final,
      )[0];
      setDefaultFinalCardio(finalCardio);

      setToggle((prev) => !prev);
    }
  }, [division, cardioVariations]);

  useEffect(() => {
    fetchControlPanel();
  }, [fetchControlPanel]);

  function StudentInfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal
        isOpen={showStudentInfoModal}
        setOpen={setShowStudentInfoModal}
        maxWidthStyled={600}
      >
        <S.ModalContainer>
          <h1>Informações do Treino</h1>

          <S.ContentContainer>
            <S.RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </S.RowContent>

            <S.RowContent>
              <strong>Programa: </strong>
              {programId}
            </S.RowContent>

            <S.RowContent>
              <strong>Nome do aluno: </strong>
              {studentData.name}
            </S.RowContent>

            <S.RowContent>
              <strong>Sexo: </strong>
              {genderVariations[studentData.student.genre]}
            </S.RowContent>

            <S.RowContent>
              <strong>Nível do aluno: </strong>
              {program?.datasheet?.student_level}
            </S.RowContent>

            <S.RowContent>
              <strong>Avaliação: </strong>
              {program?.datasheet?.classification}
            </S.RowContent>

            <S.RowContent>
              <strong>Objetivo: </strong>
              {program?.datasheet?.objective}
            </S.RowContent>

            <S.RowContent>
              <strong>Frequência: </strong>
              {program?.datasheet?.frequency}
            </S.RowContent>

            <S.RowContent>
              <strong>Tempo disponível: </strong>
              {program?.datasheet?.available_time}
            </S.RowContent>

            <S.RowContent>
              <strong>Divisão de treino: </strong>
              {program?.training_division}
            </S.RowContent>
          </S.ContentContainer>

          <div className="profile-code">
            <h2>Profile Code:</h2>
            <h3>{formatProfileCode(program?.datasheet?.profile_code)}</h3>
          </div>

          <div className="sheet-model">
            <h2>Modelo de ficha:</h2>
            <h3>{program?.data_model}</h3>
          </div>
        </S.ModalContainer>
      </Modal>
    );
  }

  const selectStyle = {
    placeholder: () => ({
      padding: '0 1rem',
      color: '#00000080',
    }),
    singleValue: () => ({
      padding: '0 1rem',
    }),
  };

  const handleSubmit = useCallback(
    async (data: IControlPanelForm) => {
      setIsLoading(true);

      const formData = {
        ...data,
        interval_between_sequence: intervalBetweenSequency,
        status: true,
        training_division_id: division.id,
        workout_time:
          workoutTime === 0 ? '00:00:00' : secondsToStringFormat(workoutTime),
        training_end: trainingEnd,
        training_start: trainingStart,
        time_of_execution:
          trainingTimeOfExecution === 0
            ? '00:00:00'
            : secondsToStringFormat(trainingTimeOfExecution),
      };

      const schema = Yup.object().shape({
        cardio_aerobic: Yup.string(),
        cardio_aerobic_final: Yup.string(),
        interval_between_sequence: Yup.number().required(
          'O campo é obrigatório',
        ),
        number_of_repetitions: Yup.number().required('O campo é obrigatório '),
        number_of_series: Yup.number().required('O campo é obrigatório '),
        recovery_total: Yup.string(),
        time_of_execution: Yup.string().required('O campo é obrigatório'),
        total_rest: Yup.string(),
        total_time: Yup.string(),
        training_division_id: Yup.number(),
        training_end: Yup.string(),
        training_start: Yup.string(),
        workout_time: Yup.string().required('O campo é obrigatório'),
        observations: Yup.string(),
      });

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.put(`/controlpanels/${panelOfControlId}`, formData);

        toast.success(`Treino finalizada com sucesso!`);

        goBack();
      } catch (err: any) {
        console.log(err);

        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);

        toast.error('Preencha os campos obrigatórios e tente novamente!');
      }

      setIsLoading(false);
    },
    [
      division,
      goBack,
      intervalBetweenSequency,
      panelOfControlId,
      trainingEnd,
      trainingStart,
      trainingTimeOfExecution,
      workoutTime,
    ],
  );

  useEffect(() => {
    setTrainingTimeOfExecutionDefaultValue(
      formatHMS(trainingStart + trainingEnd),
    );
  }, [trainingStart, trainingEnd]);

  useEffect(() => {
    const trainingTime = getSeconds(trainingTimeOfExecutionDefaultValue);
    const workout = getSeconds(workoutTimeDefaultValue);
    const recovery = getSeconds(recoveryTotalDefaultValue);
    const rest = getSeconds(totalRestDefaultValue);

    const total = trainingTime + workout + recovery + rest;

    // console.log('trainingTime:', trainingTimeOfExecutionDefaultValue);
    // console.log('workout:', workoutTimeDefaultValue);
    // console.log('recovery:', recoveryTotalDefaultValue);
    // console.log('rest:', totalRestDefaultValue);
    // console.log('total:', formatHMS(total));

    setTotalTimeDefaultValue(formatHMS(total));

    workout &&
    recovery &&
    trainingTime &&
    rest &&
    total > getSeconds(studentAvailableTime || '')
      ? setToggleTotalTime(true)
      : setToggleTotalTime(false);
  }, [
    trainingTimeOfExecutionDefaultValue,
    workoutTimeDefaultValue,
    recoveryTotalDefaultValue,
    studentAvailableTime,
    totalRestDefaultValue,
  ]);

  useEffect(() => {
    const formattedValue = getSeconds(recoveryTotalDefaultValue);
    setRecoveryTotal(formattedValue);
  }, [recoveryTotalDefaultValue]);

  useEffect(() => {
    const formattedValue = getSeconds(workoutTimeDefaultValue);
    setWorkoutTime(formattedValue);
  }, [workoutTimeDefaultValue]);

  useEffect(() => {
    const formattedValue = getSeconds(totalTimeDefaultValue);
    setTotalTime(formattedValue);
  }, [totalTimeDefaultValue]);

  return (
    <>
      <S.PageHeader>
        <S.HeaderWrapper>
          <S.FirstScreenHeaderContainer>
            <S.PageHeaderBackButton onClick={() => goBack()}>
              <IoIosArrowBack />

              <S.PageHeaderBackButtonLabel>
                protocolo de treino
              </S.PageHeaderBackButtonLabel>
            </S.PageHeaderBackButton>

            <S.FinishTrainingButton
              disabled={isLoading}
              onClick={() => formRef.current?.submitForm()}
            >
              Finalizar Treino
            </S.FinishTrainingButton>
          </S.FirstScreenHeaderContainer>

          <S.Info>
            <S.InfoContent>
              <strong>Nome: </strong>
              <p>{studentData.name}</p>
            </S.InfoContent>

            <StudentInfoModal />

            <S.InfoButton
              type="button"
              onClick={() => setShowStudentInfoModal(true)}
            >
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </S.InfoButton>
          </S.Info>

          <S.Info>
            <S.InfoContent>
              <strong>DIVISÃO: </strong>
              <p>{division.division}</p>
            </S.InfoContent>

            <S.InfoContent>
              <strong>Segmento: </strong>
              <p>{division.muscle_group}</p>
            </S.InfoContent>
          </S.Info>
        </S.HeaderWrapper>
      </S.PageHeader>

      <S.Container>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
          <div className="form-inputs">
            <S.InputTimeFormSection>
              <label htmlFor="interval_between_sequence">
                Intervalo entre troca de sequência
              </label>
              <TimeInput
                disabled={!hasTwoOrMoreSequencies}
                defaultValue={initialData?.interval_between_sequence || ''}
                setValue={setIntervalBetweenSequency}
              />
            </S.InputTimeFormSection>

            <S.FormSection>
              <label htmlFor="number_of_series">
                Volume total - n° de séries
              </label>
              <Input
                name="number_of_series"
                id="number_of_series"
                disabled
                placeholder="9"
              />
            </S.FormSection>

            <S.FormSection>
              <label htmlFor="number_of_repetitions">
                Volume total - n° de repetições
              </label>
              <Input
                name="number_of_repetitions"
                id="number_of_repetitions"
                placeholder="9"
                disabled
              />
            </S.FormSection>

            <S.FormSelect>
              <label htmlFor="cardio_aerobic">
                Cardio/aeróbico: Início do treino
              </label>
              <Select
                key={`rerender_key_${
                  defaultInitialCardio && defaultInitialCardio.label
                }`}
                name="cardio_aerobic"
                id="cardio_aerobic"
                options={cardioVariations}
                defaultValue={defaultInitialCardio ?? cardioVariations[0]}
                styles={selectStyle}
                isSearchable={false}
              />
            </S.FormSelect>

            <S.InputTimeFormSection>
              <label htmlFor="training_start">Início do treino</label>
              <TimeInput
                defaultValue={initialData.training_start}
                setValue={setTrainingStart}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="time_of_execution">
                Tempo total Cardio/aeróbico
              </label>
              <TimeInput
                disabled
                defaultValue={trainingTimeOfExecutionDefaultValue}
                setValue={setTrainingTimeOfExecution}
              />
            </S.InputTimeFormSection>

            <S.FormSelect>
              <label htmlFor="cardio_aerobic_final">
                Cardio/aeróbico: Final do treino
              </label>
              <Select
                key={`rerender_key_${
                  defaultFinalCardio && defaultFinalCardio.label
                }`}
                name="cardio_aerobic_final"
                id="cardio_aerobic_final"
                options={cardioVariations}
                defaultValue={defaultFinalCardio ?? cardioVariations[0]}
                styles={selectStyle}
                isSearchable={false}
              />
            </S.FormSelect>

            <S.InputTimeFormSection>
              <label htmlFor="training_end">Final do treino</label>
              <TimeInput
                defaultValue={initialData.training_end}
                setValue={setTrainingEnd}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="workout_time">Workout time</label>
              <TimeInput
                disabled
                defaultValue={workoutTimeDefaultValue}
                setValue={setWorkoutTime}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="recovery_total">
                Recuperação total (entre séries)
              </label>
              <TimeInput
                disabled
                defaultValue={recoveryTotalDefaultValue}
                setValue={setRecoveryTotal}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="total_rest">
                Intervalo total (entre trocas de sequência)
              </label>
              <TimeInput
                disabled
                defaultValue={totalRestDefaultValue}
                setValue={setTotalRest}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="" className={toggleTotalTime ? 'red-label' : ''}>
                {toggleTotalTime ? (
                  <p>
                    O Tempo total de Treino ultrapassou o tempo disponível do
                    aluno! ({studentAvailableTime})
                  </p>
                ) : (
                  'Tempo total'
                )}
              </label>
              <TimeInput
                disabled
                defaultValue={totalTimeDefaultValue}
                setValue={setTotalTime}
              />
            </S.InputTimeFormSection>
          </div>

          <div className="full-row-input">
            <S.FormSection>
              <label htmlFor="observations">Observações</label>
              <Textarea
                placeholder="Sem observações"
                name="observations"
                id="observations"
                containerStyle={{
                  padding: '12rem 2rem',
                }}
              />
            </S.FormSection>
          </div>
        </Form>
      </S.Container>
    </>
  );
}
